import React, { useState, useEffect } from 'react';
import './Project.css';
import Navbar from '../Navbar/Navbar.jsx';
import TopBar from '../Navbar/Mainbar.jsx';
import Swal from 'sweetalert2';

const ROWS_PER_PAGE = 10;

/* ─── helpers ─────────────────────────────────────────────── */
const getBackendUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'production') return 'https://api.leave-portal.manurevasolutions.com';
  return 'http://localhost:5000';
};

const API = getBackendUrl();

const apiFetch = (path, opts = {}) =>
  fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...opts.headers },
    ...opts
  });

const handleYearLimit = (e) => {
  const value = e.target.value;
  if (value) {
    const parts = value.split('-');
    if (parts[0] && parts[0].length > 4) {
      parts[0] = parts[0].slice(0, 4);
      e.target.value = parts.join('-');
    }
  }
};

const statusClass = (s = '') => {
  if (s.toLowerCase() === 'active')    return 'active';
  if (s.toLowerCase() === 'completed') return 'completed';
  if (s.toLowerCase() === 'on hold')   return 'on-hold';
  return 'active';
};

const EMPTY_FORM = {
  name:'', code:'', client:'', teamSize:'', manager:'',
  startDate:'', endDate:'', status:'Active', billable:true, description:''
};

/* ─── component ───────────────────────────────────────────── */
const ProjectManagement = () => {
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [selProject,    setSelProject]    = useState(null);
  const [projects,      setProjects]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFilter,  setStatusFilter]  = useState('All Status');
  const [managerUsers,  setManagerUsers]  = useState([]);
  const [loadingMgr,    setLoadingMgr]    = useState(false);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [dateError,     setDateError]     = useState('');
  const [teamSizeError, setTeamSizeError] = useState('');

  // ── pagination ───────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── sorting ──────────────────────────────────────────────
  const [sortField, setSortField] = useState('');
  const [sortDir,   setSortDir]   = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'#ffffff60', marginLeft:'4px', fontSize:'11px' }}>⇅</span>;
    return <span style={{ marginLeft:'4px', fontSize:'11px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const [managerDropdownOpen, setManagerDropdownOpen] = useState(false);
  const [statusDropdownOpen,  setStatusDropdownOpen]  = useState(false);

  /* ── load data ─────────────────────────────────────────── */
  useEffect(() => { loadProjects(); loadManagers(); }, []);

  const loadProjects = async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch('/api/projects');
      if (!r.ok) throw new Error(`Failed to fetch projects: ${r.statusText}`);
      setProjects(await r.json());
      setCurrentPage(1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadManagers = async () => {
    setLoadingMgr(true);
    try {
      const r = await apiFetch('/api/managers-list');
      if (r.ok) {
        const all = await r.json();
        setManagerUsers(all.filter(u => ['Manager','Superadmin'].includes(u.role)));
      }
    } catch {}
    finally { setLoadingMgr(false); }
  };

  /* ── filter + sort ─────────────────────────────────────── */
  const filtered = projects
    .filter(p => {
      const s = searchTerm.toLowerCase();
      const ok = p.name?.toLowerCase().includes(s) ||
                 p.code?.toLowerCase().includes(s) ||
                 p.client?.toLowerCase().includes(s);
      const okStatus = statusFilter === 'All Status' || p.status === statusFilter;
      return ok && okStatus;
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      if (sortField === 'sno') {
        const indexA = projects.indexOf(a);
        const indexB = projects.indexOf(b);
        return sortDir === 'asc' ? indexA - indexB : indexB - indexA;
      }
      let aVal = '', bVal = '';
      if (sortField === 'name')     { aVal = a.name || '';     bVal = b.name || ''; }
      if (sortField === 'client')   { aVal = a.client || '';   bVal = b.client || ''; }
      if (sortField === 'manager')  { aVal = a.manager || '';  bVal = b.manager || ''; }
      if (sortField === 'teamSize') { aVal = String(a.teamSize || ''); bVal = String(b.teamSize || ''); }
      if (sortField === 'timeline') { aVal = a.startDate || ''; bVal = b.startDate || ''; }
      if (sortField === 'billable') { aVal = a.billable ? 'Yes' : 'No'; bVal = b.billable ? 'Yes' : 'No'; }
      if (sortField === 'status')   { aVal = a.status || '';   bVal = b.status || ''; }
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

  // ── pagination calculations ───────────────────────────────
  const totalRecords = filtered.length;
  const totalPages   = Math.max(1, Math.ceil(totalRecords / ROWS_PER_PAGE));
  const safePage     = Math.min(currentPage, totalPages);
  const startIndex   = (safePage - 1) * ROWS_PER_PAGE;
  const endIndex     = Math.min(startIndex + ROWS_PER_PAGE, totalRecords);
  const paginated    = filtered.slice(startIndex, endIndex);

  // reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left  = Math.max(1, safePage - delta);
    const right = Math.min(totalPages, safePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  /* ── pagination styles ───────────────────────────────────── */
  const paginationStyles = {
    wrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 20px',
      borderTop: '1px solid #e5e7eb',
      flexWrap: 'wrap',
      gap: '12px',
    },
    info: {
      fontSize: '13px',
      color: '#6b7280',
    },
    controls: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    btn: (active, disabled) => ({
      minWidth: '32px',
      height: '32px',
      padding: '0 8px',
      border: active ? '1.5px solid #3b82f6' : '1px solid #e5e7eb',
      borderRadius: '6px',
      background: active ? '#3b82f6' : disabled ? '#f9fafb' : '#fff',
      color: active ? '#fff' : disabled ? '#d1d5db' : '#374151',
      fontSize: '13px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontWeight: active ? '500' : '400',
      transition: 'all 0.15s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    ellipsis: {
      minWidth: '32px',
      height: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '13px',
      color: '#9ca3af',
    },
  };

  /* ── modal helpers ─────────────────────────────────────── */
  const openAdd = () => {
    setEditMode(false); setSelProject(null);
    setForm(EMPTY_FORM); setDateError(''); setTeamSizeError('');
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditMode(true); setSelProject(p);
    setForm({
      name: p.name||'', code: p.code||'', client: p.client||'',
      teamSize: p.teamSize||'', manager: p.manager||'',
      startDate: p.startDate||'', endDate: p.endDate||'',
      status: p.status||'Active',
      billable: p.billable !== undefined ? p.billable : true,
      description: p.description||''
    });
    setDateError(''); setTeamSizeError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false); setSelProject(null); setForm(EMPTY_FORM);
    setDateError(''); setTeamSizeError('');
    setManagerDropdownOpen(false);
    setStatusDropdownOpen(false);
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updated = { ...form, [name]: type === 'checkbox' ? checked : value };
    if (name === 'startDate' || name === 'endDate') {
      const start = updated.startDate;
      const end   = updated.endDate;
      if (start && end && start > end) setDateError('Start date cannot be later than end date.');
      else setDateError('');
    }
    if (name === 'teamSize') {
      if (value !== '' && (isNaN(value) || parseInt(value) < 1)) setTeamSizeError('Team size must be at least 1.');
      else setTeamSizeError('');
    }
    setForm(updated);
  };

  /* ── CRUD ──────────────────────────────────────────────── */
  const handleSubmit = async () => {
    if (!form.name || !form.code || !form.client) { alert('Please fill in all required fields'); return; }
    if (form.startDate && form.endDate && form.startDate > form.endDate) {
      setDateError('Start date cannot be later than end date.'); return;
    }
    if (form.teamSize !== '' && (isNaN(form.teamSize) || parseInt(form.teamSize) < 1)) {
      setTeamSizeError('Team size must be at least 1.'); return;
    }
    try {
  const url    = editMode ? `/api/projects/${selProject.id}` : '/api/projects';
  const method = editMode ? 'PUT' : 'POST';
  const r = await apiFetch(url, { method, body: JSON.stringify(form) });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Failed to save project'); }
  const result = await r.json();
  await loadProjects();
  closeModal();

  Swal.fire({
    html: `
      <div style="padding: 1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Project ${editMode ? 'updated' : 'created'}</p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">The project was ${editMode ? 'updated' : 'created'} successfully.</p>
      </div>
    `,
    width: '340px',
    confirmButtonText: 'OK',
     confirmButtonColor: '#111111',
    customClass: {
      popup: 'swal-employee rounded-2xl',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
    }
  });

} catch (e) {
  Swal.fire({
    html: `
      <div style="padding: 1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Something went wrong</p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${e.message || 'Something went wrong while saving the project.'}</p>
      </div>
    `,
    width: '340px',
    confirmButtonText: 'Try again',
     confirmButtonColor: '#111111',
    customClass: {
      popup: 'swal-employee rounded-2xl',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
    }
  });
}
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
  html: `
    <div style="padding: 1rem 0.5rem">
      <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Are you sure?</p>
      <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">You won't be able to revert this!</p>
    </div>
  `,
  width: '340px',
  showCancelButton: true,
  confirmButtonText: 'Yes, delete it!',
  cancelButtonText: 'Cancel',
   confirmButtonColor: '#111111',
  customClass: {
    popup: 'swal-employee rounded-2xl',
    actions: 'gap-2',
    confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5 bg-red-600 text-white hover:bg-red-700',
    cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 bg-white text-gray-500 border border-gray-200 hover:bg-gray-50',
  }
});
    if (!result.isConfirmed) return;
   try {
  const r = await apiFetch(`/api/projects/${id}`, { method: 'DELETE' });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Failed to delete'); }
  await loadProjects();

  Swal.fire({
    html: `
      <div style="padding: 1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Deleted</p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">The project has been successfully removed.</p>
      </div>
    `,
    width: '340px',
    confirmButtonText: 'OK',
    confirmButtonColor: '#111111',
    customClass: {
      popup: 'swal-employee rounded-2xl',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
    }
  });

} catch (e) {
  Swal.fire({
    html: `
      <div style="padding: 1rem 0.5rem">
        <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </div>
        <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Something went wrong</p>
        <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">${e.message || 'Failed to delete project.'}</p>
      </div>
    `,
    width: '340px',
    confirmButtonText: 'Close',
    confirmButtonColor: '#111111',
    customClass: {
      popup: 'swal-employee rounded-2xl',
      confirmButton: 'rounded-sm text-sm font-medium px-6',
    }
  });
}
  };

  const handleExport = () => {
  try {
    if (filtered.length === 0) {
      Swal.fire({
        html: `
          <div style="padding: 1rem 0.5rem">
            <div style="width:56px;height:56px;border-radius:50%;background:#fffbeb;border:1.5px solid #fde68a;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Nothing to export</p>
            <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">No projects match the current filters.</p>
          </div>
        `,
        width: '340px',
        confirmButtonText: 'OK',
        confirmButtonColor: '#111111',
        customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
      });
      return;
    }

    const headers = ['S.No', 'Project Name', 'Project Code', 'Client', 'Team Size', 'Manager', 'Start Date', 'End Date', 'Status'];
    const rows = filtered.map((p, index) => [
      index + 1, p.name, p.code, p.client, p.teamSize || '', p.manager || '',
      p.startDate || '', p.endDate || '', p.status
    ]);
    const [year, month, day] = new Date().toISOString().split('T')[0].split('-');
    const filename = `projects_${month}-${day}-${year}.csv`;
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: filename
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);

    // ✅ Success
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#f0fdf4;border:1.5px solid #bbf7d0;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Export Successful</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Project data has been exported as <strong>${filename}</strong></p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'OK',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });

  } catch {
    // ✅ Error
    Swal.fire({
      html: `
        <div style="padding: 1rem 0.5rem">
          <div style="width:56px;height:56px;border-radius:50%;background:#fef2f2;border:1.5px solid #fecaca;display:flex;align-items:center;justify-content:center;margin:0 auto 1.25rem">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <p style="font-size:17px;font-weight:600;color:#111;margin:0 0 0.5rem">Export Failed</p>
          <p style="font-size:13px;color:#6b7280;line-height:1.6;margin:0">Something went wrong while exporting. Please try again.</p>
        </div>
      `,
      width: '340px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#111111',
      customClass: { popup: 'swal-employee rounded-2xl', confirmButton: 'rounded-sm text-sm font-medium px-6' }
    });
  }
};

  /* ─── render ───────────────────────────────────────────── */
  return (
    <div className="Admin-Project-navbar" style={{ overflow:'hidden' }}>
      <div className="Admin-proj-nav"><Navbar /></div>

      <div className="Admin-Project-container">
        <TopBar />

        <div className="proj-body">

          <div className="Admin-Project-header">
            <div className="Admin-Project-title-section">
              <h1>Project Management</h1>
              <p className="Admin-Project-subtitle">Manage all projects and assignments</p>
            </div>
          </div>

          <button className="Admin-Project-add-btn" onClick={openAdd}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="8" x2="12" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <line x1="8" y1="12" x2="16" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Add Project
          </button>

          {error && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', color:'#dc2626', fontSize:13 }}>
              {error} —{' '}
              <button onClick={loadProjects} style={{ color:'#dc2626', fontWeight:700, background:'none', border:'none', cursor:'pointer', textDecoration:'underline' }}>Retry</button>
            </div>
          )}

          <div className="proj-filter-bar">
            <div className="proj-search-wrap">
              <svg width="13" height="13" fill="none" viewBox="0 0 20 20">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4.35-4.35" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <input
                placeholder="Search by name, code, client…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="proj-filter-right">
              {!loading && (
                <span className="proj-count-badge">
                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/>
                    <path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                  </svg>
                  {totalRecords} projects
                </span>
              )}
              <select className="proj-status-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option>All Status</option>
                <option>Active</option>
                <option>Completed</option>
                <option>On Hold</option>
              </select>
              <button className="proj-export-btn" onClick={handleExport}>
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Export
              </button>
            </div>
          </div>

          <div className="proj-table-section">
            <h2 className="proj-table-title">
              {loading ? 'Projects' : `Project Details (${totalRecords})`}
            </h2>
            <div className="proj-table-wrapper">
              {loading ? (
                <div className="proj-state-box"><p>Loading projects…</p></div>
              ) : totalRecords === 0 ? (
                <div className="proj-state-box">
                  <svg width="48" height="48" fill="none" viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="2" stroke="#d1d5db" strokeWidth="1.5"/>
                    <path d="M3 9h18M9 21V9" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <p>No projects found</p>
                  <small>
                    {projects.length === 0
                      ? 'Click "Add Project" to create your first project'
                      : 'Try adjusting your search or filter criteria'}
                  </small>
                </div>
              ) : (
                <>
                  <table className="proj-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('sno')} style={{ cursor:'pointer', userSelect:'none' }}>
                          S.No <SortIcon field="sno" />
                        </th>
                        <th onClick={() => handleSort('name')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Project <SortIcon field="name" />
                        </th>
                        <th onClick={() => handleSort('client')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Client <SortIcon field="client" />
                        </th>
                        <th onClick={() => handleSort('manager')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Manager <SortIcon field="manager" />
                        </th>
                        <th onClick={() => handleSort('teamSize')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Team Size <SortIcon field="teamSize" />
                        </th>
                        <th onClick={() => handleSort('timeline')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Timeline <SortIcon field="timeline" />
                        </th>
                        <th onClick={() => handleSort('status')} style={{ cursor:'pointer', userSelect:'none' }}>
                          Status <SortIcon field="status" />
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((p, pageIndex) => {
                        // const globalIndex = startIndex + pageIndex;
                        // const displayIndex = sortField === 'sno' && sortDir === 'desc'
                        //   ? totalRecords - globalIndex
                        //   : globalIndex + 1;
                        const displayIndex = projects.indexOf(p) + 1;
                        return (
                          <tr key={p.id}>
                            <td>{displayIndex}</td>
                            <td>
                              <div className="proj-name-cell">
                                <span className="proj-name">{p.name}</span>
                                <span className="proj-code">{p.code}</span>
                              </div>
                            </td>
                            <td>{p.client}</td>
                            <td>{p.manager || '—'}</td>
                            <td>{p.teamSize || '—'}</td>
                            <td style={{ whiteSpace:'nowrap', fontSize:13 }}>
                              {p.timeline || (p.startDate && p.endDate ? `${p.startDate} – ${p.endDate}` : '—')}
                            </td>
                            <td>
                              <span className={`proj-status-badge ${statusClass(p.status)}`}>{p.status}</span>
                            </td>
                            <td>
                              <div className="proj-actions">
                                <button className="proj-action-btn edit" onClick={() => openEdit(p)} title="Edit project">
                                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                <button className="proj-action-btn delete" onClick={() => handleDelete(p.id)} title="Delete project">
                                  <svg width="13" height="13" fill="none" viewBox="0 0 24 24">
                                    <polyline points="3 6 5 6 21 6" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* ── Pagination Bar ── */}
                  {totalPages > 1 && (
                    <div style={paginationStyles.wrapper}>
                      <span style={paginationStyles.info}>
                        Showing {startIndex + 1}–{endIndex} of {totalRecords} projects
                      </span>
                      <div style={paginationStyles.controls}>
                        <button
                          style={paginationStyles.btn(false, safePage === 1)}
                          onClick={() => goToPage(safePage - 1)}
                          disabled={safePage === 1}
                          title="Previous page"
                        >
                          ‹
                        </button>
                        {getPageNumbers().map((page, idx) =>
                          page === '...'
                            ? <span key={`ellipsis-${idx}`} style={paginationStyles.ellipsis}>…</span>
                            : (
                              <button
                                key={page}
                                style={paginationStyles.btn(page === safePage, false)}
                                onClick={() => goToPage(page)}
                              >
                                {page}
                              </button>
                            )
                        )}
                        <button
                          style={paginationStyles.btn(false, safePage === totalPages)}
                          onClick={() => goToPage(safePage + 1)}
                          disabled={safePage === totalPages}
                          title="Next page"
                        >
                          ›
                        </button>
                      </div>
                    </div>
                  )}

                  {totalPages === 1 && totalRecords > 0 && (
                    <div style={{ ...paginationStyles.wrapper, justifyContent: 'flex-end' }}>
                      <span style={paginationStyles.info}>
                        {totalRecords} project{totalRecords !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

        </div>

        {/* Modal */}
        {modalOpen && (
          <div className="Admin-Project-modal-overlay">
            <div className="Admin-Project-modal">
              <div className="Admin-Project-modal-header">
                <h2 className="Admin-Project-modal-title">
                  {editMode ? 'Edit Project' : 'Add New Project'}
                </h2>
                <button className="Admin-Project-modal-close" onClick={closeModal}>×</button>
              </div>

              <div className="Admin-Project-modal-body">

                {[
                  { label:'Project Name', name:'name',   ph:'Enter project name', req:true },
                  { label:'Project Code', name:'code',   ph:'e.g., AXB-LOS-2025', req:true },
                  { label:'Client Name',  name:'client', ph:'Enter client name',   req:true },
                ].map(({ label, name, ph, req }) => (
                  <div className="Admin-Project-form-group" key={name}>
                    <label className="Admin-Project-form-label">
                      {label} {req && <span className="Admin-Project-required">*</span>}
                    </label>
                    <input type="text" name={name} placeholder={ph} className="Admin-Project-form-input"
                      value={form[name]} onChange={onChange} />
                  </div>
                ))}

                {[
                  { label:'Start Date', name:'startDate', req:true },
                  { label:'End Date',   name:'endDate',   req:true },
                ].map(({ label, name, req }) => (
                  <div className="Admin-Project-form-group" key={name}>
                    <label className="Admin-Project-form-label">
                      {label} {req && <span className="Admin-Project-required">*</span>}
                    </label>
                    <input type="date" name={name} className="Admin-Project-form-input"
                      value={form[name]} onChange={onChange} max="9999-12-31" onInput={handleYearLimit} />
                    {name === 'endDate' && dateError && (
                      <span style={{ color:'#dc2626', fontSize:12, marginTop:4, display:'block' }}>
                        {dateError}
                      </span>
                    )}
                  </div>
                ))}

                <div className="Admin-Project-form-group">
                  <label className="Admin-Project-form-label">Team Size</label>
                  <input type="number" name="teamSize" placeholder="e.g., 5" min="1"
                    className="Admin-Project-form-input" value={form.teamSize} onChange={onChange} />
                  {teamSizeError && (
                    <span style={{ color:'#dc2626', fontSize:12, marginTop:4, display:'block' }}>
                      {teamSizeError}
                    </span>
                  )}
                </div>

                {/* Manager dropdown */}
                <div className="Admin-Project-form-group" style={{ position:'relative' }}>
                  <label className="Admin-Project-form-label">
                    Project Manager <span className="Admin-Project-required">*</span>
                  </label>
                  <div
                    className="Admin-Project-form-select"
                    onClick={() => !loadingMgr && setManagerDropdownOpen(o => !o)}
                    style={{
                      cursor: loadingMgr ? 'not-allowed' : 'pointer',
                      display:'flex', justifyContent:'space-between', alignItems:'center',
                      userSelect:'none'
                    }}
                  >
                    <span style={{ color: form.manager ? 'inherit' : '#9ca3af' }}>
                      {loadingMgr
                        ? 'Loading managers…'
                        : form.manager
                          ? managerUsers.find(m => m.name === form.manager)
                              ? `${form.manager} (${managerUsers.find(m => m.name === form.manager).role})`
                              : form.manager
                          : 'Select Project Manager'}
                    </span>
                  </div>
                  {managerDropdownOpen && (
                    <>
                      <div style={{ position:'fixed', inset:0, zIndex:999 }} onClick={() => setManagerDropdownOpen(false)} />
                      <ul style={{
                        position:'absolute', top:'100%', left:0, right:0, zIndex:1000,
                        background:'#fff', border:'1px solid #e5e7eb', borderRadius:8,
                        boxShadow:'0 4px 16px rgba(0,0,0,0.12)', margin:'4px 0 0',
                        padding:0, listStyle:'none', maxHeight:220, overflowY:'auto'
                      }}>
                        <li
                          onClick={() => { setForm(f => ({ ...f, manager:'' })); setManagerDropdownOpen(false); }}
                          style={{ padding:'10px 14px', cursor:'pointer', color:'#9ca3af', fontSize:14, background:!form.manager?'#f0f4ff':'transparent' }}
                          onMouseEnter={e => e.currentTarget.style.background='#f9fafb'}
                          onMouseLeave={e => e.currentTarget.style.background=!form.manager?'#f0f4ff':'transparent'}
                        >
                          Select Project Manager
                        </li>
                        {managerUsers.map(m => (
                          <li
                            key={m.id}
                            onClick={() => { setForm(f => ({ ...f, manager:m.name })); setManagerDropdownOpen(false); }}
                            style={{ padding:'10px 14px', cursor:'pointer', fontSize:14, background:form.manager===m.name?'#f0f4ff':'transparent' }}
                            onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                            onMouseLeave={e => e.currentTarget.style.background=form.manager===m.name?'#f0f4ff':'transparent'}
                          >
                            {m.name} <span style={{ color:'#9ca3af' }}>({m.role})</span>
                          </li>
                        ))}
                        {managerUsers.length === 0 && !loadingMgr && (
                          <li style={{ padding:'10px 14px', color:'#9ca3af', fontSize:14 }}>No managers found</li>
                        )}
                      </ul>
                    </>
                  )}
                  {managerUsers.length === 0 && !loadingMgr && (
                    <span className="proj-hint">No manager/HR users available.</span>
                  )}
                </div>

                {/* Status dropdown */}
                <div className="Admin-Project-form-group" style={{ position:'relative' }}>
                  <label className="Admin-Project-form-label">
                    Status <span className="Admin-Project-required">*</span>
                  </label>
                  <div
                    className="Admin-Project-form-select"
                    onClick={() => setStatusDropdownOpen(o => !o)}
                    style={{ cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', userSelect:'none' }}
                  >
                    <span>{form.status || 'Select Status'}</span>
                  </div>
                  {statusDropdownOpen && (
                    <>
                      <div style={{ position:'fixed', inset:0, zIndex:999 }} onClick={() => setStatusDropdownOpen(false)} />
                      <ul style={{
                        position:'absolute', top:'100%', left:0, right:0, zIndex:1000,
                        background:'#fff', border:'1px solid #e5e7eb', borderRadius:8,
                        boxShadow:'0 4px 16px rgba(0,0,0,0.12)', margin:'4px 0 0',
                        padding:0, listStyle:'none', overflow:'hidden'
                      }}>
                        {['Active','Completed','On Hold'].map(option => (
                          <li
                            key={option}
                            onClick={() => { setForm(f => ({ ...f, status:option })); setStatusDropdownOpen(false); }}
                            style={{ padding:'10px 14px', cursor:'pointer', fontSize:14, background:form.status===option?'#f0f4ff':'transparent' }}
                            onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                            onMouseLeave={e => e.currentTarget.style.background=form.status===option?'#f0f4ff':'transparent'}
                          >
                            {option}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>

                <div className="Admin-Project-form-group">
                  <label className="Admin-Project-form-label">Description</label>
                  <textarea name="description" placeholder="Project description and objectives…"
                    className="Admin-Project-form-textarea" rows="4"
                    value={form.description} onChange={onChange} />
                </div>

              </div>

              <div className="Admin-Project-modal-footer">
                <button className="Admin-Project-cancel-btn" onClick={closeModal}>Cancel</button>
                <button className="Admin-Project-submit-btn" onClick={handleSubmit}>
                  {editMode ? 'Update Project' : 'Add Project'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProjectManagement;
